import { useEffect, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useReferralCoachmarkStore } from './referral-coachmark-store';

const AUTO_DISMISS_MS = 6000;

/**
 * One-time discovery coachmark shown after the user first dismisses the referral
 * pill: tells them Refer & Earn now lives in the profile menu (top-right). Fixed
 * top-right under the header, pointing up at the avatar. Auto-dismisses after 6s
 * or on the next interaction; shown once ever (store-gated).
 */
export function ReferralCoachmark(): JSX.Element | null {
  const { t } = useTranslation();
  const open = useReferralCoachmarkStore((s) => s.open);
  const hide = useReferralCoachmarkStore((s) => s.hide);

  useEffect(() => {
    if (!open) return;
    const timer = window.setTimeout(hide, AUTO_DISMISS_MS);
    const onInteract = (): void => hide();
    window.addEventListener('pointerdown', onInteract, { once: true });
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('pointerdown', onInteract);
    };
  }, [open, hide]);

  if (!open) return null;

  return (
    <div
      data-testid="referral-coachmark"
      role="status"
      className="fixed right-4 top-14 z-50 max-w-[240px] rounded-lg bg-foreground px-3 py-2 text-sm font-medium text-background shadow-md"
    >
      <span className="absolute -top-1 right-6 h-2 w-2 rotate-45 bg-foreground" />
      {t('referral.coachmark.title')}
    </div>
  );
}
